// ============================================
// LIGA AJUPAM - App Resume Hook (v6.11.43)
// ============================================
// Ejecuta un callback cuando la app "vuelve a la vida": la pestaña/PWA recupera
// visibilidad o foco, o el dispositivo recupera conexión. Opcionalmente también
// cada N ms mientras la app está visible.
//
// Por qué existe: en la PWA instalada, cuando queda en segundo plano el sistema
// congela timers y suele matar el websocket de Supabase Realtime. Realtime NO
// tiene replay: todo lo que cambió mientras la app dormía no llega nunca. Sin un
// refetch al volver, la única salida era recargar a mano (síntoma reportado al
// arrancar la temporada: la app abierta no reconocía la zona recién asignada).
//
// El patrón ya existía copiado a mano en varios lugares (index.tsx para el SW,
// AdminDashboard, OpeningWizard, usePushNotifications). Este hook lo unifica para
// los consumidores nuevos, con throttle incluido.

import { useEffect, useRef } from 'react';

interface UseAppResumeOptions {
  /** Mínimo entre dos ejecuciones, en ms (default: 60s). Evita el spam de `focus`. */
  throttleMs?: number;
  /** Además, ejecutar cada N ms mientras la app está visible. 0 = desactivado. */
  intervalMs?: number;
  /** Activar/desactivar la suscripción (default: true). */
  enabled?: boolean;
}

export const useAppResume = (
  callback: () => void,
  { throttleMs = 60_000, intervalMs = 0, enabled = true }: UseAppResumeOptions = {}
): void => {
  // Ref para no re-suscribir en cada render cuando el callback no es estable.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const lastRunRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    // Arrancar el throttle "ya usado": los datos recién se cargaron al montar, y
    // en escritorio un `focus` inmediato dispararía un refetch al pedo.
    if (lastRunRef.current === 0) lastRunRef.current = Date.now();

    const run = () => {
      // Nunca refrescar en segundo plano: el fetch quedaría congelado igual y
      // consumiría la ventana del throttle sin traer nada.
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRunRef.current < throttleMs) return;
      lastRunRef.current = now;
      callbackRef.current();
    };

    document.addEventListener('visibilitychange', run);
    window.addEventListener('focus', run);
    window.addEventListener('online', run);
    const timer = intervalMs > 0 ? setInterval(run, intervalMs) : null;

    return () => {
      document.removeEventListener('visibilitychange', run);
      window.removeEventListener('focus', run);
      window.removeEventListener('online', run);
      if (timer) clearInterval(timer);
    };
  }, [enabled, throttleMs, intervalMs]);
};
